import { useQuery } from "@tanstack/react-query";
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { clsx } from "clsx";
import { Timer } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { devMetricsApi, type CeleryAnalytics } from "../api/devMetrics";

function fmtMs(v: number | null): string {
  if (v == null) return "—";
  if (v >= 60000) return `${(v / 60000).toFixed(1)} min`;
  if (v >= 1000) return `${(v / 1000).toFixed(2)} s`;
  return `${v.toFixed(0)} ms`;
}

function fmtTick(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const d = new Date(label);
  const t = isNaN(d.getTime()) ? label : fmtTick(label);
  return (
    <div className="bg-gray-900/95 border border-gray-700 rounded-lg px-3 py-2 text-xs shadow-xl min-w-[150px]">
      <p className="text-gray-400 mb-1.5 font-medium">{t}</p>
      {payload.map((p: { name: string; value: number | null; color: string }, i: number) => (
        <div key={i} className="flex items-center gap-2 mb-0.5">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
          <span className="text-gray-300">{p.name}:</span>
          <span className="text-white font-semibold tabular-nums">{fmtMs(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

const th = "px-2 py-1.5 text-left font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide";
const td = "px-2 py-1 tabular-nums text-gray-700 dark:text-gray-300";

/** Async Celery telemetry — p95/avg execution time per task type, per-user
 *  breakdown and queue-wait (publish→start). Data from CeleryTaskRun. */
export function CeleryTimingSection({ hours, enabled }: { hours: number; enabled: boolean }) {
  const { data, isLoading } = useQuery<CeleryAnalytics>({
    queryKey: ["dev-metrics-celery-analytics", hours],
    queryFn: () => devMetricsApi.getCeleryAnalytics(hours),
    refetchInterval: 60_000,
    enabled,
  });

  const byType = data?.by_type ?? [];
  const byUser = data?.by_user ?? [];
  const ts = data?.timeseries ?? [];

  return (
    <section className="mt-6">
      <div className="flex items-center gap-2 mb-4">
        <Timer size={16} className="text-[var(--color-primary)]" />
        <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
          Celery — czasy tasków async (p95, kolejka, per typ/user)
        </h2>
      </div>

      {isLoading && (
        <div className="h-40 flex items-center justify-center">
          <Spinner />
        </div>
      )}

      {!isLoading && byType.length === 0 && (
        <div className="h-24 flex items-center justify-center text-sm text-gray-400 text-center px-4">
          Brak danych — telemetria per-run zbierana jest od momentu wdrożenia; pojawi się,
          gdy zaczną wykonywać się taski Celery.
        </div>
      )}

      {!isLoading && byType.length > 0 && (
        <div className="space-y-4">
          {/* Timeseries: p95 czas wykonania + p95 czas w kolejce */}
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              p95 czas wykonania & p95 oczekiwanie w kolejce (per min)
            </p>
            <ResponsiveContainer width="100%" height={200}>
              <ComposedChart data={ts} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" strokeOpacity={0.3} />
                <XAxis dataKey="ts" tickFormatter={fmtTick} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={52} tickFormatter={(v) => fmtMs(v as number)} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} formatter={(v) => <span style={{ color: "#9ca3af" }}>{v}</span>} />
                <Line type="monotone" dataKey="p95_ms" name="p95 wykonanie" stroke="#3b82f6" strokeWidth={1.5} dot={false} connectNulls />
                <Line type="monotone" dataKey="p95_wait_ms" name="p95 kolejka" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {/* Per typ taska (sort po p95) */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Per typ taska — najwolniejsze wg p95
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className={th}>Task</th>
                      <th className={clsx(th, "text-right")}>n</th>
                      <th className={clsx(th, "text-right")}>avg</th>
                      <th className={clsx(th, "text-right")}>p95</th>
                      <th className={clsx(th, "text-right")}>kolejka p95</th>
                      <th className={clsx(th, "text-right")}>błędy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byType.map((r) => (
                      <tr key={r.task_name} className="border-b border-gray-100 dark:border-gray-700/50">
                        <td className={clsx(td, "font-mono text-[11px] max-w-[220px] truncate")} title={r.task_name}>
                          {r.task_name.replace(/^apps\./, "")}
                        </td>
                        <td className={clsx(td, "text-right")}>{r.n}</td>
                        <td className={clsx(td, "text-right")}>{fmtMs(r.avg_ms)}</td>
                        <td className={clsx(td, "text-right font-semibold")}>{fmtMs(r.p95_ms)}</td>
                        <td className={clsx(td, "text-right")}>{fmtMs(r.p95_wait_ms)}</td>
                        <td className={clsx(td, "text-right", r.failures > 0 && "text-red-500 font-semibold")}>{r.failures || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Per user (sort po liczbie) */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Per użytkownik — kto generuje taski
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className={th}>Użytkownik</th>
                      <th className={clsx(th, "text-right")}>n</th>
                      <th className={clsx(th, "text-right")}>avg</th>
                      <th className={clsx(th, "text-right")}>p95</th>
                      <th className={clsx(th, "text-right")}>błędy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byUser.map((r) => (
                      <tr key={r.user_id ?? "system"} className="border-b border-gray-100 dark:border-gray-700/50">
                        <td className={clsx(td, "max-w-[200px] truncate", !r.user_id && "italic text-gray-400")} title={r.user}>
                          {r.user}
                        </td>
                        <td className={clsx(td, "text-right")}>{r.n}</td>
                        <td className={clsx(td, "text-right")}>{fmtMs(r.avg_ms)}</td>
                        <td className={clsx(td, "text-right font-semibold")}>{fmtMs(r.p95_ms)}</td>
                        <td className={clsx(td, "text-right", r.failures > 0 && "text-red-500 font-semibold")}>{r.failures || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <p className="text-[10px] font-mono text-gray-400 dark:text-gray-500">
            czas wykonania = prerun→postrun · kolejka = publish→start (opóźnienie brokera Redis) ·
            „System / beat" = taski bez użytkownika (harmonogram, łańcuchy, retry)
          </p>
        </div>
      )}
    </section>
  );
}
